import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, HelpCircle, Plus, Pencil, Star, Home, Eye, EyeOff, ArrowUp, ArrowDown,
  Trash2, Globe, CircleSlash, Sparkles,
} from "lucide-react";

const NAVY = "#1C4358", GREEN = "#2F7D5A", RED = "#9C1E22", AMBER = "#B7791F";
const STATUS_HEX = { pending: AMBER, approved: GREEN, rejected: RED };

const SERVICE_LANE_OPTIONS = [
  { value: "", label: "— None —" },
  { value: "sell_home", label: "Selling the Home" },
  { value: "home_safety", label: "Keep Loved One Home Safely" },
  { value: "move_into_my_home", label: "Move Into My Home" },
  { value: "senior_placement", label: "Find Senior Living" },
  { value: "downsizing", label: "Downsizing & Estate" },
  { value: "home_repairs", label: "Home Repairs & Accessibility" },
];

const EMPTY = {
  question: "", suggested_answer: "", category: "", related_service_lane: "",
  related_reverse_topic: "", keywords: "", seo_title: "", seo_description: "",
  featured: false, show_on_homepage: false,
};

const Badge = ({ children, color, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
    style={{ backgroundColor: `${color}18`, color }}>{children}</span>
);

export default function FamilyFaqManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // id | null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/qa/items");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load FAQs (admin only).");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const categories = data?.categories || [];
  const catLabel = (k) => categories.find((c) => c.key === k)?.label || "Uncategorised";
  const allItems = data?.items || [];
  const visible = allItems
    .filter((it) => (cat === "all" ? true : it.category === cat))
    .filter((it) => (status === "all" ? true : it.status === status))
    .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const setB = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  const act = async (id, fn, okMsg) => {
    setB(id, true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setB(id, false); }
  };

  const publish = (it) => act(it.id, () => api.post(`/qa/items/${it.id}/approve`), "FAQ published");
  const unpublish = (it) => act(it.id, () => api.post(`/qa/items/${it.id}/reject`), "FAQ unpublished");
  const toggleFeatured = (it) => act(it.id, () => api.put(`/qa/items/${it.id}`, { featured: !it.featured }));
  const toggleHomepage = (it) => act(it.id, () => api.put(`/qa/items/${it.id}`, { show_on_homepage: !it.show_on_homepage }));
  const toggleVisible = (it) => act(it.id, () => api.put(`/qa/items/${it.id}`, { active: it.active === false }));
  const remove = (it) => {
    if (!window.confirm("Archive this FAQ? It will no longer appear publicly.")) return;
    act(it.id, () => api.delete(`/qa/items/${it.id}`), "FAQ archived");
  };

  const reorder = async (list) => {
    try {
      await api.post("/qa/faq/reorder", { ids: list.map((x) => x.id) });
      await load();
    } catch (e) { toast.error("Could not save order."); }
  };
  const move = (idx, dir) => {
    const arr = [...visible];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    reorder(arr);
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      question: it.question || "", suggested_answer: it.suggested_answer || "",
      category: it.category || "", related_service_lane: it.related_service_lane || "",
      related_reverse_topic: it.related_reverse_topic || "",
      keywords: (it.tags || []).join(", "), seo_title: it.seo_title || "",
      seo_description: it.seo_description || "",
      featured: !!it.featured, show_on_homepage: !!it.show_on_homepage,
    });
    setOpen(true);
  };

  const payload = () => ({
    question: form.question.trim(),
    suggested_answer: form.suggested_answer.trim(),
    category: form.category || null,
    related_service_lane: form.related_service_lane || null,
    related_reverse_topic: form.related_reverse_topic.trim() || null,
    tags: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
    seo_title: form.seo_title.trim() || null,
    seo_description: form.seo_description.trim() || null,
    featured: !!form.featured,
    show_on_homepage: !!form.show_on_homepage,
  });

  const save = async (alsoPublish) => {
    if (!form.question.trim()) { toast.error("A question is required."); return; }
    if (alsoPublish && !form.suggested_answer.trim()) { toast.error("Add an answer before publishing."); return; }
    setSaving(true);
    try {
      let id = editing;
      if (editing) {
        await api.put(`/qa/items/${editing}`, payload());
      } else {
        const { data } = await api.post("/qa/items", payload());
        id = data.item.id;
      }
      if (alsoPublish && id) await api.post(`/qa/items/${id}/approve`);
      toast.success(alsoPublish ? "FAQ saved & published" : "FAQ saved");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save FAQ.");
    } finally {
      setSaving(false);
    }
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="family-faq-manager">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#9C1E22]"><HelpCircle size={20} /><span className="text-sm font-semibold uppercase tracking-wide">Family FAQ</span></div>
          <h1 className="font-display text-3xl font-bold text-[#252221] mt-1">Family FAQ Manager</h1>
          <p className="text-[15px] text-[#6b7480] mt-1 max-w-2xl">Warm, voice-search-friendly answers for families. Publish to show them on the homepage, the full FAQ page, and matching service pages.</p>
        </div>
        <Button data-testid="faq-mgr-new" onClick={openNew} className="text-white" style={{ backgroundColor: RED }}>
          <Plus size={16} className="mr-1.5" /> New FAQ
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <button data-testid="faq-mgr-cat-all" onClick={() => setCat("all")}
          className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${cat === "all" ? "border-[#1C4358] bg-[#1C4358] text-white" : "border-[#1C4358]/20 text-[#1C4358] hover:bg-[#1C4358]/6"}`}>All</button>
        {categories.map((c) => (
          <button key={c.key} data-testid={`faq-mgr-cat-${c.key}`} onClick={() => setCat(c.key)}
            className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${cat === c.key ? "border-[#1C4358] bg-[#1C4358] text-white" : "border-[#1C4358]/20 text-[#1C4358] hover:bg-[#1C4358]/6"}`}>
            {c.label} <span className="opacity-70">{c.count}</span>
          </button>
        ))}
        <div className="ml-auto w-44">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="faq-mgr-status" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="approved">Published</SelectItem>
              <SelectItem value="pending">Draft / Pending</SelectItem>
              <SelectItem value="rejected">Unpublished</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#1C4358]" size={28} /></div>
      ) : visible.length === 0 ? (
        <Card className="p-10 text-center text-[#6b7480]" data-testid="faq-mgr-empty">No FAQs here yet. Tap “New FAQ” to add one.</Card>
      ) : (
        <div className="space-y-2.5">
          {visible.map((it, idx) => (
            <Card key={it.id} data-testid={`faq-mgr-row-${it.id}`} className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex flex-col gap-1 pt-0.5">
                  <button data-testid={`faq-mgr-up-${it.id}`} onClick={() => move(idx, -1)} disabled={idx === 0}
                    className="text-[#9AA5B1] hover:text-[#1C4358] disabled:opacity-30"><ArrowUp size={15} /></button>
                  <button data-testid={`faq-mgr-down-${it.id}`} onClick={() => move(idx, 1)} disabled={idx === visible.length - 1}
                    className="text-[#9AA5B1] hover:text-[#1C4358] disabled:opacity-30"><ArrowDown size={15} /></button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-[15px] text-[#1C4358]">{it.question}</span>
                    <Badge color={STATUS_HEX[it.status] || NAVY} testid={`faq-mgr-badge-status-${it.id}`}>{it.status}</Badge>
                    {it.featured && <Badge color={AMBER} testid={`faq-mgr-badge-featured-${it.id}`}><Star size={11} /> Featured</Badge>}
                    {it.show_on_homepage && <Badge color={GREEN} testid={`faq-mgr-badge-home-${it.id}`}><Home size={11} /> Homepage</Badge>}
                    {it.active === false && <Badge color={RED} testid={`faq-mgr-badge-hidden-${it.id}`}><EyeOff size={11} /> Hidden</Badge>}
                    <span className="text-[12px] text-[#9AA5B1]">{catLabel(it.category)}</span>
                  </div>
                  {it.suggested_answer && <p className="mt-1 text-[13px] text-[#6b7480] line-clamp-2">{it.suggested_answer}</p>}
                  {(it.tags || []).length > 0 && (
                    <p className="mt-1 text-[12px] text-[#9AA5B1]">Keywords: {(it.tags || []).join(", ")}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" data-testid={`faq-mgr-edit-${it.id}`} onClick={() => openEdit(it)}><Pencil size={14} /></Button>
                  <Button size="sm" variant="outline" data-testid={`faq-mgr-feature-${it.id}`} onClick={() => toggleFeatured(it)} title="Toggle featured"
                    className={it.featured ? "text-[#B7791F] border-[#B7791F]/40" : ""}><Star size={14} /></Button>
                  <Button size="sm" variant="outline" data-testid={`faq-mgr-homepage-${it.id}`} onClick={() => toggleHomepage(it)} title="Toggle homepage"
                    className={it.show_on_homepage ? "text-[#2F7D5A] border-[#2F7D5A]/40" : ""}><Home size={14} /></Button>
                  <Button size="sm" variant="outline" data-testid={`faq-mgr-visibility-${it.id}`} onClick={() => toggleVisible(it)} title="Show / hide">
                    {it.active === false ? <EyeOff size={14} /> : <Eye size={14} />}
                  </Button>
                  {it.status === "approved" ? (
                    <Button size="sm" variant="outline" data-testid={`faq-mgr-unpublish-${it.id}`} onClick={() => unpublish(it)} className="text-[#9C1E22] border-[#9C1E22]/40">
                      <CircleSlash size={14} className="mr-1" /> Unpublish
                    </Button>
                  ) : (
                    <Button size="sm" data-testid={`faq-mgr-publish-${it.id}`} onClick={() => publish(it)} className="text-white" style={{ backgroundColor: GREEN }} disabled={busy[it.id]}>
                      {busy[it.id] ? <Loader2 size={13} className="animate-spin mr-1" /> : <Globe size={14} className="mr-1" />} Publish
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" data-testid={`faq-mgr-delete-${it.id}`} onClick={() => remove(it)} className="text-[#9C1E22]"><Trash2 size={14} /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="faq-mgr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><Sparkles size={18} className="text-[#9C1E22]" /> {editing ? "Edit FAQ" : "New FAQ"}</DialogTitle>
            <DialogDescription>Keep answers warm, short, and honest. No legal, medical, financial, or real-estate guarantees.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[14px] font-semibold text-[#1C4358]">Question</Label>
              <Input data-testid="faq-field-question" value={form.question} onChange={(e) => setF("question", e.target.value)} placeholder="e.g. How much does a consultation cost?" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[14px] font-semibold text-[#1C4358]">Answer</Label>
              <Textarea data-testid="faq-field-answer" rows={4} value={form.suggested_answer} onChange={(e) => setF("suggested_answer", e.target.value)} placeholder="A warm, short, conversational answer…" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[14px] font-semibold text-[#1C4358]">Category / Topic</Label>
                <Select value={form.category || "none"} onValueChange={(v) => setF("category", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="faq-field-category"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[14px] font-semibold text-[#1C4358]">Service lane (optional)</Label>
                <Select value={form.related_service_lane || "none"} onValueChange={(v) => setF("related_service_lane", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="faq-field-lane"><SelectValue placeholder="Match a service page" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_LANE_OPTIONS.map((o) => <SelectItem key={o.value || "none"} value={o.value || "none"}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[14px] font-semibold text-[#1C4358]">Reverse-search topic (optional)</Label>
                <Input data-testid="faq-field-reverse" value={form.related_reverse_topic} onChange={(e) => setF("related_reverse_topic", e.target.value)} placeholder="e.g. sell-moms-house" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[14px] font-semibold text-[#1C4358]">Voice-search keywords</Label>
                <Input data-testid="faq-field-keywords" value={form.keywords} onChange={(e) => setF("keywords", e.target.value)} placeholder="comma, separated, phrases" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[14px] font-semibold text-[#1C4358]">SEO title (optional)</Label>
                <Input data-testid="faq-field-seo-title" value={form.seo_title} onChange={(e) => setF("seo_title", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[14px] font-semibold text-[#1C4358]">SEO description (optional)</Label>
                <Input data-testid="faq-field-seo-desc" value={form.seo_description} onChange={(e) => setF("seo_description", e.target.value)} />
              </div>
            </div>
            <div className="flex flex-wrap gap-6 pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox data-testid="faq-field-featured" checked={form.featured} onCheckedChange={(v) => setF("featured", !!v)} />
                <span className="text-[14px] text-[#1C4358] font-medium">Featured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox data-testid="faq-field-homepage" checked={form.show_on_homepage} onCheckedChange={(v) => setF("show_on_homepage", !!v)} />
                <span className="text-[14px] text-[#1C4358] font-medium">Show on homepage</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="faq-mgr-cancel">Cancel</Button>
              <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="faq-mgr-save">Save draft</Button>
              <Button onClick={() => save(true)} disabled={saving} className="text-white" style={{ backgroundColor: GREEN }} data-testid="faq-mgr-save-publish">
                {saving ? <Loader2 size={15} className="animate-spin mr-1" /> : <Globe size={15} className="mr-1" />} Save &amp; Publish
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
